<template>
  <div
    :class="{
      'order-summary-sticky-bar t-center': true,
      'order-summary-sticky-bar-visible': showBar && canCheckout
    }"
  >
    <p class="order-summary-subscription-product">
      {{ subscriptionInfo }}
    </p>
    <div class="order-summary-subscription-price">
      <p
        :class="{
          'price mb-0': true,
          'with-discount': hasDiscount && userData.coffee?.sku !== undefined
        }"
      >
        <span
          v-if="userData.amount === 0 && userData.coffee?.sku !== undefined"
          class="variant-indicator"
        >
          From
        </span>
        £{{ parseFloat(displayPrice).toFixed(2) }}
      </p>
      <p v-if="hasDiscount && userData.coffee.sku !== undefined" class="price-without-discount mb-0">
        was (
        <span style="text-decoration-line: line-through;">£{{ parseFloat(originalPrice).toFixed(2) }}</span>
        )
      </p>
    </div>
    <div v-if="showVoucher" class="order-summary-voucher">
      <div class="voucher-message-banner">
        <span>Offer applied: {{ voucherMessage }} </span>
      </div>
    </div>
    <button
      :class="{
        'btn order-summary-cta mx-auto w-100': true,
        'disabled btn-primary-light-blue': !canCheckout,
        'btn-secondary disable-active disable-focus disable-hover': canCheckout
      }"
      @click="goToCheckout"
    >
      Go to checkout
    </button>
  </div>
</template>

<script setup>
import { inject, computed, ref, onMounted, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'

const store = useStore()
const userData = inject('userData')
const voucherData = inject('voucherData')
const funnel_version = inject('funnel_version')
const router = useRouter()

const summaryOutOfView = ref(false)
let observer = null

const coffees_range = computed(() => {
  return store.getters['shop/coffees_range']
})

const coffee_to_use = computed(() => {
  var coffee_to_use = undefined
  if (userData.value?.coffee?.sku !== undefined) coffee_to_use = coffees_range.value.find((coffee) => coffee.sku === userData.value.coffee.sku.split('-')[0])
  return coffee_to_use
})

const coffee_plans = computed(() => {
  var plans = []
  coffee_to_use.value?.coffee_plans.forEach((plan) => {
    const allowed_plans_amounts = [ '250g', '1Kg' ]
    if(allowed_plans_amounts.includes(Object.keys(plan)[0]))
      plans.push({ ...plan[Object.keys(plan)[0]], amount_display: Object.keys(plan)[0].toLowerCase() })
  })
  return plans.sort((a, b) => {
    if(a.amount > b.amount) return 1
    else if(a.amount < b.amount) return -1
    return 0
  })
})

const subscriptionInfo = computed(() => {
  const coffee = userData.value?.coffee?.name
  const amount = userData.value?.amount
  const frequency = userData.value?.frequency
  if (!coffee || !amount) return 'Select your coffee and size'
  const amountLabel = amount >= 1000 ? amount / 1000 + 'kg' : amount + 'g'
  return funnel_version.value === 'D' ? `${coffee} • ${amountLabel}` : `${coffee} • ${amountLabel} • Every ${frequency} days`
})

const voucherMessage = computed(() => {
  return voucherData.value?.voucher?.offer_title +' '+voucherData.value.voucher?.offer_description
})

const displayPrice = computed(() => {
  var plan_sku = userData.value.plan?.sku
  var total = userData.value?.amount === 0 ? userData.value.is_pioneer ? userData.value.plan.pioneer_price : userData.value.plan.price : userData.value?.total
  var plan = coffee_plans.value.find((plan) => plan.coffee_plan_sku === plan_sku)
  if(plan !== undefined){
    var plan_price = plan.discounted_price !== null ? plan.discounted_price : plan.price
    var plan_pioneer_price = plan.pioneer_discounted_price !== null ? plan.pioneer_discounted_price : plan.pioneer_price
    total = userData.value.is_pioneer ? plan_pioneer_price : plan_price
  }
  return total || 0
})

const originalPrice = computed(() => {
  var price = userData.value?.plan?.price
  return price || 0
})

const hasDiscount = computed(() => {
  var plan_sku = userData.value.plan?.sku
  var discount = userData.value?.discount
  var plan = coffee_plans.value.find((plan) => plan.coffee_plan_sku === plan_sku)
  if(plan !== undefined)
    discount = userData.value.is_pioneer ? plan.pioneer_price_discount : plan.price_discount
  return discount !== 0 && discount !== null || userData.value.is_pioneer
 })

const showVoucher = computed(() => {
  return voucherData.value?.voucher_code !== ''
})

const disableCta = computed(() => {
  return (
    userData.value?.bean_type !== undefined &&
    userData.value?.brewing_method !== undefined &&
    userData.value?.plan?.sku !== undefined &&
    userData.value?.coffee?.sku !== undefined &&
    userData.value?.amount > 0
  )
})

const disableCtaB = computed(() => {
  return (
    userData.value?.bean_type !== undefined &&
    userData.value?.brewing_method !== undefined &&
    userData.value?.plan?.sku !== undefined &&
    userData.value?.coffee?.sku !== undefined &&
    userData.value?.amount > 0 &&
    userData.value?.frequency !== undefined
  )
})

const canCheckout = computed(() => {
  return funnel_version.value === 'D' ? disableCtaB.value : disableCta.value
})

// Only pin the bar once the user is able to proceed to checkout
const showBar = computed(() => {
  return summaryOutOfView.value && canCheckout.value
})

function goToCheckout() {
  if (!canCheckout.value) return
  router.push({ name: 'checkout' })
}

onMounted(() => {
  const summaryEl = document.querySelector('#signupC .order-summary')
  if (!summaryEl) return
  // Mirror the Basket sticky button: show the bar whenever the summary is out of the viewport
  observer = new IntersectionObserver(([entry]) => {
    summaryOutOfView.value = !entry.isIntersecting
  })
  observer.observe(summaryEl)
})

onBeforeUnmount(() => {
  observer?.disconnect()
})
</script>

<script>
export default {
  name: 'OrderSummaryStickyBtn',
}
</script>

<style lang="scss" scoped>
.order-summary-sticky-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100; // below navbar (101), above page content
  background: #fff;
  padding: 15px 20px;
  padding-bottom: calc(15px + env(safe-area-inset-bottom));
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.12);
  transform: translateY(110%);
  transition: transform 0.3s ease;

  @media (min-width: 992px) {
    display: none;
  }

  &-visible {
    transform: translateY(0);
  }

  .order-summary-subscription-product {
    color: #7A7C7D;
    font-family: 'Apercu';
    font-size: 16px;
    font-weight: 300;
    line-height: 24px;
    margin-bottom: 5px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .order-summary-subscription-price {
    margin-bottom: 15px;

    .price-without-discount {
      color: #212427;
      font-family: 'Apercu';
      font-size: 20px;
      font-weight: 300;
      line-height: 24px;
    }

    .price {
      color: #212427;
      font-family: 'Apercu';
      font-size: 36px;
      font-weight: 700;
      line-height: 42px;

      &.with-discount {
        color: #D94822;
      }

      .variant-indicator {
        color: #212427;
        font-family: Apercu;
        font-size: 16px;
        font-style: normal;
        font-weight: 300;
        line-height: 16px;
        letter-spacing: 0px;
        padding-right: 5px;
        position: relative;
        top: -5px;
      }
    }
  }

  .order-summary-voucher {
    margin-bottom: 15px;

    .voucher-message-banner {
      display: inline-block;
      border: 1px solid #D6F2ED;
      border-radius: 50px;
      padding: 6px 16px;
      background-color: #EBF9F6;

      span{
        color: #30BEA6;
        font-family: 'Apercu-medium';
        font-size: 14px;
        font-weight: 500;
        line-height: 18px;
      }
    }
  }

  .order-summary-cta {
    min-width: 280px;
    max-width: 330px;
    height: 50px;
    line-height: 50px;
    font-size: 18px;
    border-radius: 8px;
    padding: 0;

    &.disabled {
      opacity: 1;
    }
  }
}
</style>
